// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// PROCURAR NO PLANO por escrito — a lógica da paleta de comandos (ponto 3)
// ============================================================================
//
// 📌 **Tudo o que a paleta faz já existe.** Há seis janelas na barra e 250 × 365 células; o que
//    falta é **um sítio onde se escreve em vez de se procurar**. `234` e chega-se à viatura,
//    `15 ago` e chega-se ao dia, `IGT` e filtra-se o cliente.
//
// ⚠️⚠️ **A PALETA SÓ NAVEGA. NÃO ESCREVE NADA.** 📌 É a decisão que a torna de risco nulo, e não
//    é uma limitação temporária: um campo de texto que aceita `reservar 234 15 ago` é a forma
//    mais rápida de reservar a viatura errada. Reservar exige ver o mapa, ver as opções e — se
//    divergir de um limite legal — escrever um motivo (D-007, art. 10.º n.º 3). Nada disso cabe
//    numa linha de texto, e fingir que cabe seria trocar prudência por velocidade no único gesto
//    do sistema onde isso não se pode fazer.
//
// ⚠️ **É lógica PURA, aqui e não no componente**, pela mesma razão de `substituicao.ts` e
//    `porque.ts`: o que decide o que aparece primeiro numa lista de resultados é a peça que se
//    parte em silêncio, e testá-la a abrir janelas num navegador é testá-la mal.
//
// ⚠️ **Os DIACRÍTICOS são normalizados nas duas direcções** — quem escreve depressa escreve
//    `aviacao` e `sao joao`; quem cola de um email traz `Aviação` e `São João`.
//
// ⚠️⚠️ **E normalizar remove DIACRÍTICOS, não letras.** 📌 `Aviação` fica `aviacao`: o `ç` vira
//    `c`, não desaparece — logo `aviao` **não** encontra «Aviação», e está certo que não encontre.
//    Fazê-lo casar exigia procura aproximada, que não foi pedida e traria falsos positivos numa
//    lista de 312 motoristas. ⚠️ Eu escrevi um teste a exigir o contrário, e ele falhou com razão.
// ============================================================================
import { dataUtcDoIndice, janelaDe } from './janela-do-mapa.js';
import { indiceDaData, dentroDaJanela, dataDoIndice } from './janela-do-mapa.js';
/**
 * Minúsculas e **sem acentos**, para comparar.
 *
 * ⚠️⚠️ **O intervalo dos diacríticos escreve-se com ESCAPES e não com os caracteres literais.**
 * 📌 A primeira versão tinha-os literais — são marcas combinatórias, isto é **invisíveis no
 * ficheiro** — e o que ficou lá não era o intervalo que eu quis: `aviao` deixava de encontrar
 * «Aviação». ⚠️ E não é erro de nada: a expressão regular é válida, compila, corre, e
 * simplesmente não casa nada. Apanhado por teste, que é a única forma de o apanhar.
 *
 * ⚠️ `NFD` separa a letra do diacrítico e o intervalo `̀-ͯ` remove-o. É o único sítio
 * onde isto se faz — com duas normalizações diferentes, `Avião` e `aviao` deixavam de ser a mesma
 * coisa dependendo de quem comparava.
 */
export function normalizar(s) {
    return s
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();
}
/** Pontos por qualidade da coincidência. Exacto > começa por > contém. */
function pontuar(agulha, palha) {
    const a = normalizar(agulha);
    const p = normalizar(palha);
    if (a === '' || p === '')
        return 0;
    if (p === a)
        return 100;
    if (p.startsWith(a))
        return 60;
    if (p.includes(a))
        return 30;
    return 0;
}
/**
 * Prioridade entre tipos, para desempatar pontuações iguais.
 *
 * ⚠️ **É uma escolha declarada, não uma ordem natural.** Um planeador escreve números de viatura e
 * datas muito mais vezes do que nomes de localidade; com o empate desfeito ao acaso, a lista
 * mudava de ordem entre teclas e obrigava a ler tudo outra vez.
 */
const PRIORIDADE = {
    // ⚠️ O combinado vem sempre à frente: honra as duas coisas que a pessoa escreveu.
    combinado: 0,
    viatura: 1,
    dia: 2,
    cliente: 3,
    motorista: 4,
    subcontratado: 5,
    itinerario: 6,
    localidade: 7,
};
const MESES = [
    'janeiro',
    'fevereiro',
    'marco',
    'abril',
    'maio',
    'junho',
    'julho',
    'agosto',
    'setembro',
    'outubro',
    'novembro',
    'dezembro',
];
const DIAS_DA_SEMANA = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
/**
 * Interpreta o texto como uma DATA e devolve o índice do dia no ano — ou `-1`.
 *
 * Formas aceites: `15 ago`, `15 agosto`, `15/8`, `15-08`, `2026-08-15`, `15.8`.
 *
 * ⚠️⚠️ **`234` sozinho NÃO é uma data**, e é a decisão que faz esta função útil. Um número solto é
 * um número de viatura — é o que os planeadores escrevem, e interpretá-lo como dia 234 do ano
 * levava o mapa para Agosto sempre que alguém procurasse uma viatura. 📌 Uma data precisa de
 * **duas partes**: dia e mês.
 *
 * ⚠️ E o ano é o do MAPA, não o de hoje: o plano é anual e o mapa mostra um ano só. Aceitar
 * `2027-03-04` e devolver um dia deste ano era responder à pergunta errada em silêncio — devolve
 * `-1`, e a paleta simplesmente não oferece o resultado.
 */
export function interpretarDia(texto, dados) {
    const t = normalizar(texto);
    if (t === '')
        return -1;
    // ---- `2026-08-15` e `2026/08/15`
    const iso = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/.exec(t);
    if (iso !== null) {
        // 🔴 D-101 · uma data com ano é procurada na FITA (que pode ter dois anos), e fora dela é `-1`.
        const isoData = `${iso[1]}-${String(iso[2]).padStart(2, '0')}-${String(iso[3]).padStart(2, '0')}`;
        if (Number.isNaN(Date.parse(`${isoData}T00:00:00Z`)))
            return -1;
        const janela = janelaDe(dados);
        const i = indiceDaData(janela, isoData);
        return dentroDaJanela(janela, i) && dataDoIndice(janela, i) === isoData ? i : -1;
    }
    // ---- `15/8`, `15-08`, `15.8`
    const numerico = /^(\d{1,2})[-/.](\d{1,2})$/.exec(t);
    if (numerico !== null) {
        return diaDoAno(dados, Number(numerico[2]) - 1, Number(numerico[1]));
    }
    // ---- `15 ago`, `15 agosto`, `15ago`
    const porNome = /^(\d{1,2})\s*([a-z]{3,})$/.exec(t);
    if (porNome !== null) {
        const prefixo = porNome[2] ?? '';
        // ⚠️ Prefixo e não igualdade: `ago`, `agos` e `agosto` são a mesma intenção. E **um só**
        //    mês pode corresponder — `ma` casa com `março` e `maio`, e escolher um dos dois em
        //    silêncio levava o mapa a um sítio que ninguém pediu.
        const casam = MESES.map((m, i) => [m, i]).filter(([m]) => m.startsWith(prefixo));
        if (casam.length !== 1)
            return -1;
        return diaDoAno(dados, casam[0]?.[1] ?? -1, Number(porNome[1]));
    }
    return -1;
}
/**
 * Procura o índice do dia com este mês e dia-do-mês. `-1` se não existir (31 de Fevereiro).
 * ⚠️ D-101 · numa fita com dois anos, o mesmo dia-mês aparece duas vezes: devolve o PRIMEIRO. Quem
 * quer o outro escreve o ano (`2027-08-15`).
 */
function diaDoAno(dados, mes, diaDoMes) {
    if (mes < 0 || mes > 11 || diaDoMes < 1 || diaDoMes > 31)
        return -1;
    for (let d = 0; d < dados.nDias; d++) {
        if (dados.mesPorDia[d] === mes && dados.diaDoMes[d] === diaDoMes)
            return d;
    }
    return -1;
}
/** `sábado, 15 de agosto` — para a linha de detalhe de um resultado de dia. */
export function descreverDia(dados, dia) {
    const mes = MESES[dados.mesPorDia[dia] ?? 0] ?? '';
    // ⚠️ O dia da semana vem de `fimDeSemana` **e** do calendário: `Date` em UTC, porque o resto do
    //    mapa também o faz e um deslocamento de fuso mudava o rótulo de metade dos dias.
    const d = dataUtcDoIndice(janelaDe(dados), dia);
    const semana = DIAS_DA_SEMANA[d.getUTCDay()] ?? '';
    return `${semana}, ${dados.diaDoMes[dia]} de ${mes}`;
}
/**
 * Procura no plano. **Só devolve sítios onde ir e coisas para realçar; nunca acções de escrita.**
 *
 * @param limite tecto de resultados. ⚠️ Uma lista de 250 viaturas não se lê com o teclado, e o
 * objectivo da paleta é chegar depressa — não enumerar.
 */
function procurarTermo(dados, texto, limite = 12) {
    const t = normalizar(texto);
    if (t === '')
        return [];
    const r = [];
    // ---- Um DIA, se o texto se interpretar como data --------------------------
    const dia = interpretarDia(texto, dados);
    if (dia >= 0) {
        r.push({
            tipo: 'dia',
            rotulo: `Ir a ${dados.diaDoMes[dia]} de ${MESES[dados.mesPorDia[dia] ?? 0] ?? ''}`,
            detalhe: descreverDia(dados, dia),
            dia,
            // ⚠️ 100 e não 60: quando alguém escreve `15 ago` não está a procurar mais nada, e uma
            //    localidade que casasse por «contém» não pode ficar-lhe à frente.
            pontos: 100,
        });
    }
    // ---- Viaturas: por número e por matrícula --------------------------------
    for (const v of dados.viaturas) {
        const p = Math.max(pontuar(t, v.numero), pontuar(t, v.matricula));
        if (p > 0) {
            r.push({
                tipo: 'viatura',
                rotulo: `Viatura ${v.numero}`,
                detalhe: `${v.matricula} · ${v.lotacao} lugares`,
                id: v.id,
                pontos: p,
            });
        }
    }
    for (const s of dados.subcontratados) {
        const p = pontuar(t, s.nome);
        if (p > 0) {
            r.push({
                tipo: 'subcontratado',
                rotulo: s.nome,
                // 📌 Diz-se que é subcontratado porque a linha dele **não** está na frota: vive numa
                //    secção própria (D-002), e quem salta para lá tem de saber que mudou de zona.
                detalhe: 'subcontratado — secção própria abaixo da frota',
                id: s.id,
                pontos: p,
            });
        }
    }
    // ---- Clientes: sigla e nome ---------------------------------------------
    dados.clientes.forEach(([sigla, nome], i) => {
        const p = Math.max(pontuar(t, sigla), pontuar(t, nome));
        if (p > 0) {
            r.push({
                tipo: 'cliente',
                rotulo: `Filtrar ${sigla}`,
                detalhe: nome,
                indice: i,
                pontos: p,
            });
        }
    });
    dados.motoristas.forEach((nome, i) => {
        const p = pontuar(t, nome);
        if (p > 0) {
            r.push({ tipo: 'motorista', rotulo: `Filtrar ${nome}`, detalhe: null, indice: i, pontos: p });
        }
    });
    // ⚠️ **Procura pelos DOIS**: o código (`IT-BCN5PAR`) e o nome («Loire Valley»). Quem escreve na
    //    paleta de comandos pode conhecer qualquer um dos dois, e desde que o nome existe (2026-08-14)
    //    procurar só pelo código escondia metade da resposta.
    dados.itinerarios.forEach(([id, nome], i) => {
        const p = Math.max(pontuar(t, id), nome === '' ? 0 : pontuar(t, nome));
        if (p > 0) {
            r.push({
                tipo: 'itinerario',
                // O nome quando existe, e o código sempre — é o código que identifica.
                rotulo: nome === '' ? `Filtrar ${id}` : `Filtrar ${nome} (${id})`,
                detalhe: 'itinerário',
                indice: i,
                pontos: p,
            });
        }
    });
    // ---- Localidades: código e nome -----------------------------------------
    // ⚠️ **Não levam a lado nenhum, e é por isso que vêm em último.** O mapa não tem eixo
    //    geográfico: saber que `MAD` existe não diz em que linha nem em que coluna está. O que este
    //    resultado dá é o **nome completo** de um código ambíguo — `SLM` Salamanca contra `SVQ`
    //    Sevilha, `BJZ` Badajoz contra `BIO` Bilbao —, que é uma pergunta que os planeadores fazem.
    dados.localidades.forEach(([codigo, nome], i) => {
        const p = Math.max(pontuar(t, codigo), pontuar(t, nome));
        if (p > 0) {
            r.push({
                tipo: 'localidade',
                rotulo: `${codigo} — ${nome}`,
                detalhe: 'localidade',
                indice: i,
                pontos: p,
            });
        }
    });
    // ⚠️ Ordem: pontos, depois a prioridade declarada, depois o rótulo — **três** critérios, para o
    //    resultado ser o mesmo em duas execuções. Com dois, viaturas de pontuação igual trocavam de
    //    lugar entre teclas e a lista parecia mexer-se sozinha.
    r.sort((a, b) => b.pontos - a.pontos ||
        PRIORIDADE[a.tipo] - PRIORIDADE[b.tipo] ||
        a.rotulo.localeCompare(b.rotulo, 'pt'));
    return r.slice(0, limite);
}
/** O eixo de filtro de cada tipo — `null` quando o tipo nao filtra nada. */
function eixoDoTipo(t) {
    if (t === 'cliente')
        return 'clientes';
    if (t === 'motorista')
        return 'motoristas';
    if (t === 'itinerario')
        return 'rotas';
    return null;
}
/**
 * Combina dois resultados de tipos DIFERENTES num so, ou devolve `null`.
 *
 * ⚠️⚠️ **So combina o que produz uma accao mais precisa do que cada parte sozinha:**
 *
 * | Par | O que faz |
 * |---|---|
 * | viatura + dia | leva a **celula** — a linha e a coluna, que e o gesto mais util de todos |
 * | viatura + cliente/motorista/itinerario | leva a viatura **e** isola o filtro |
 * | dia + cliente/motorista/itinerario | vai ao dia **e** isola o filtro |
 *
 * 📌 **Dois do MESMO tipo nao se combinam**, e e o que protege `Maria Silva`: partido ao espaco,
 * `Maria` e `Silva` casam com o mesmo motorista, e «motorista + motorista» nao quer dizer nada. O
 * mesmo para `Iberia Grand Tours`.
 *
 * ⚠️⚠️ **Localidade nunca entra numa combinacao**: nao navega para lado nenhum (o mapa nao tem eixo
 * geografico), logo `100 SLM` nao e mais preciso do que `100` — seria um resultado a prometer o
 * que nao cumpre.
 */
function combinar(a, b) {
    // ⚠️⚠️ **As duas metades tem de ser EXACTAS ou PREFIXO (>= 60).** 📌 Uma metade que so «contem»
    //    o texto e quase sempre acidente: `ago` esta dentro de «Ti**ago**», e com isso `15 ago`
    //    oferecia «Viatura 150 · Tiago». Uma sugestao acidental no topo de uma lista que se percorre
    //    com as setas e pior do que uma sugestao a menos — leva o mapa a um sitio que ninguem pediu.
    if (Math.min(a.pontos, b.pontos) < 60)
        return null;
    if (a.tipo === b.tipo)
        return null;
    if (a.tipo === 'localidade' || b.tipo === 'localidade')
        return null;
    if (a.tipo === 'combinado' || b.tipo === 'combinado')
        return null;
    const linha = [a, b].find((x) => x.tipo === 'viatura' || x.tipo === 'subcontratado');
    const coluna = [a, b].find((x) => x.tipo === 'dia');
    const filtro = [a, b].find((x) => eixoDoTipo(x.tipo) !== null);
    // Precisa de duas partes de naturezas diferentes; sem isso nao ha nada a combinar.
    if ([linha, coluna, filtro].filter((x) => x !== undefined).length < 2)
        return null;
    const rotulo = [
        linha === undefined ? null : linha.rotulo,
        coluna === undefined ? null : coluna.rotulo.replace(/^Ir a /, ''),
        filtro === undefined ? null : filtro.rotulo.replace(/^Filtrar /, ''),
    ]
        .filter((x) => x !== null)
        .join(' · ');
    const detalhe = [
        linha === undefined ? null : 'leva a linha',
        coluna === undefined ? null : 'e ao dia',
        filtro === undefined ? null : 'com o filtro isolado',
    ]
        .filter((x) => x !== null)
        .join(' ');
    return {
        tipo: 'combinado',
        rotulo,
        detalhe,
        ...(linha === undefined ? {} : { id: linha.id }),
        ...(coluna === undefined ? {} : { dia: coluna.dia }),
        ...(filtro === undefined
            ? {}
            : { indice: filtro.indice, eixo: eixoDoTipo(filtro.tipo) }),
        // ⚠️⚠️ **A pontuacao vem da QUALIDADE DAS METADES, e nao e fixa.**
        //
        // 📌 Estava fixa em 120 — acima de tudo — e destruiu o caso `15 ago` **nos dados reais**:
        // `15` casa por prefixo com a viatura **150** e `ago` esta contido em «Ti**ago**», logo
        // «Viatura 150 · Tiago» vinha antes da data exacta de 15 de Agosto. ⚠️ Os meus dados de teste
        // nao tinham viatura 150 nem motorista com «ago» no nome: eram limpos demais para o mostrar, e
        // so apareceu ao correr a procura contra o mapa servido.
        //
        // 📌 `min + 20`: com as duas metades exactas (100) da **120** e ganha a qualquer termo unico;
        // com metades fracas fica **abaixo** de uma coincidencia exacta, que e o que tem de ser.
        pontos: Math.min(a.pontos, b.pontos) + 20,
    };
}
/**
 * Procura no plano. **So devolve sitios onde ir e coisas para realcar; nunca accoes de escrita.**
 *
 * 📗 Cliente, 2026-08-11: escreveu `100 01/1` e `100 BLV` e nao obteve nada. 📌 E as duas coisas
 * existem — a frota e 100–349 e `BLV` e a Bellavista Trips. O defeito era meu: a procura tratava a
 * **linha inteira** como um so termo, e escrever duas coisas separadas por um espaco e a forma
 * natural de dizer «a viatura 100 no dia 1 de Janeiro».
 *
 * ⚠️⚠️ Tres passagens, e a ordem e a da confianca:
 *   1. a **linha inteira** como um termo — e o que preserva `15 ago`, `Maria Silva` e `Iberia Grand
 *      Tours`, onde o espaco faz parte do nome;
 *   2. a linha **partida em dois** em cada espaco, combinando as metades quando sao de naturezas
 *      diferentes — `100 01/1`, `100 BLV`;
 *   3. se so uma metade se reconhecer, devolve-se **essa** e diz-se qual foi ignorada.
 */
export function procurar(dados, texto, limite = 12) {
    const t = texto.trim();
    if (t === '')
        return { resultados: [], ignorado: null };
    const inteiro = procurarTermo(dados, t, limite);
    // ---- 2 · partir em dois, em cada espaco --------------------------------
    const combinados = [];
    let metade = null;
    for (let i = 0; i < t.length; i++) {
        if (!/\s/.test(t.charAt(i)))
            continue;
        const esq = t.slice(0, i).trim();
        const dir = t.slice(i + 1).trim();
        if (esq === '' || dir === '')
            continue;
        const a = procurarTermo(dados, esq, 1)[0];
        const b = procurarTermo(dados, dir, 1)[0];
        if (a !== undefined && b !== undefined) {
            const c = combinar(a, b);
            if (c !== null)
                combinados.push(c);
        }
        else if (metade === null && a !== undefined) {
            // ⚠️⚠️ Guarda-se a PRIMEIRA metade reconhecida, da esquerda para a direita: e a ordem em que a
            //    pessoa escreveu, e escolher outra qualquer era escolher em silencio.
            metade = { res: procurarTermo(dados, esq, limite), ignorado: dir };
        }
        else if (metade === null && b !== undefined) {
            metade = { res: procurarTermo(dados, dir, limite), ignorado: esq };
        }
    }
    if (inteiro.length > 0 || combinados.length > 0) {
        // ⚠️⚠️ Sem duplicados: com dois espacos, o mesmo combinado pode chegar por dois caminhos, e a
        //    mesma linha repetida numa lista de 12 e um erro que se ve.
        const vistos = new Set();
        const juntos = [...combinados, ...inteiro].filter((x) => {
            const k = [x.tipo, x.id ?? '', x.dia ?? '', x.indice ?? '', x.eixo ?? ''].join(':');
            if (vistos.has(k))
                return false;
            vistos.add(k);
            return true;
        });
        juntos.sort((x, y) => y.pontos - x.pontos ||
            prioridade(x.tipo) - prioridade(y.tipo) ||
            x.rotulo.localeCompare(y.rotulo, 'pt'));
        return { resultados: juntos.slice(0, limite), ignorado: null };
    }
    // ---- 3 · so uma metade se reconheceu ------------------------------------
    if (metade !== null)
        return { resultados: metade.res, ignorado: metade.ignorado };
    return { resultados: [], ignorado: null };
}
/** O combinado vem sempre a frente; os outros pela prioridade declarada. */
function prioridade(t) {
    return t === 'combinado' ? 0 : PRIORIDADE[t];
}
//# sourceMappingURL=procura.js.map